import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Stack,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  InputAdornment,
  Button,
  FormControlLabel,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  ReceiptLong,
  Search,
  Refresh,
  Add,
  CheckCircle,
  Cancel,
  Delete,
  Edit,
  Link as LinkIcon,
} from '@mui/icons-material';
import MainText from '../../components/MainText';
import CustomSwitch from '../../components/CustomSwitch';
import CleanningButton from '../../components/CleanningButton';
import CustomModal from '../../components/CustomModal';
import CustomSnackBar from '../../components/CustomSnackBar';
import { PaymentCrud } from '../../services/api';
import { useDispatch } from 'react-redux';
import { addTab } from '../../tabs/tabsSlice';

// =========================
// Utils
// =========================
const formatCurrency = (v) => {
  try {
    return new Intl.NumberFormat('es-GT', { style: 'currency', currency: 'GTQ' }).format(Number(v || 0));
  } catch {
    const num = Number(v || 0);
    return `Q ${num.toFixed(2)}`;
  }
};

const formatDate = (d) => {
  if (!d) return '';
  try {
    const dt = new Date(d);
    const day = String(dt.getDate()).padStart(2, '0');
    const month = String(dt.getMonth() + 1).padStart(2, '0');
    const year = dt.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return String(d).slice(0, 10);
  }
};

// =========================
// Cell helpers (centrado X/Y)
// =========================
const CellCenter = ({ children }) => (
  <Box sx={{ height: '100%', width: '100%', display: 'flex', alignItems: 'center' }}>
    {children}
  </Box>
);

const CellCenterJustify = ({ children, justify = 'center' }) => (
  <Box sx={{ height: '100%', width: '100%', display: 'flex', alignItems: 'center', justifyContent: justify }}>
    {children}
  </Box>
);

// =========================
// Form (modal)
// =========================
function ReceiptForm({ initial, onSubmit }) {
  const [form, setForm] = useState({
    number: initial?.number || '',
    date: initial?.date || '',
    amount: initial?.amount ?? '',
    receipt_type: initial?.receipt_type || '',
    is_physical: initial?.is_physical ?? false,
    is_active: initial?.is_active ?? true,
    payment: initial?.payment || '',
  });

  useEffect(() => {
    setForm({
      number: initial?.number || '',
      date: initial?.date || '',
      amount: initial?.amount ?? '',
      receipt_type: initial?.receipt_type || '',
      is_physical: initial?.is_physical ?? false,
      is_active: initial?.is_active ?? true,
      payment: initial?.payment || '',
    });
  }, [initial]);

  const handleChange = (name, value) => setForm((p) => ({ ...p, [name]: value }));

  const handleSave = () => {
    const payload = {
      number: String(form.number || '').trim(),
      date: form.date || null,
      amount: Number(form.amount || 0),
      receipt_type: form.receipt_type || null,
      is_physical: Boolean(form.is_physical),
      is_active: Boolean(form.is_active),
    };
    const payId = Number(form.payment);
    if (payId) payload.payment = payId;
    onSubmit(payload);
  };

  return (
    <Stack spacing={2} sx={{ p: 1 }}>
      <TextField
        size="small"
        label="Numero de recibo"
        value={form.number}
        onChange={(e) => handleChange('number', e.target.value)}
      />

      <TextField
        size="small"
        label="Fecha"
        type="date"
        value={form.date || ''}
        onChange={(e) => handleChange('date', e.target.value)}
        InputLabelProps={{ shrink: true }}
      />

      <TextField
        size="small"
        label="Monto"
        type="number"
        inputProps={{ step: '0.01' }}
        value={form.amount}
        onChange={(e) => handleChange('amount', e.target.value)}
      />

      <TextField
        size="small"
        label="Tipo (opcional)"
        value={form.receipt_type}
        onChange={(e) => handleChange('receipt_type', e.target.value)}
      />

      <TextField
        size="small"
        label="Pago (ID opcional)"
        type="number"
        value={form.payment}
        onChange={(e) => handleChange('payment', e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LinkIcon fontSize="small" />
            </InputAdornment>
          )
        }}
      />

      <FormControlLabel
        control={
          <CustomSwitch
            name="is_physical"
            label={form.is_physical ? 'Fisico' : 'Electronico'}
            initialValue={form.is_physical}
            onChange={(_, v) => handleChange('is_physical', v)}
          />
        }
        label=""
      />

      <FormControlLabel
        control={
          <CustomSwitch
            name="is_active"
            label={form.is_active ? 'Activo' : 'Inactivo'}
            initialValue={form.is_active}
            onChange={(_, v) => handleChange('is_active', v)}
          />
        }
        label=""
      />

      <Box sx={{ textAlign: 'right' }}>
        <Button variant="contained" onClick={handleSave}>
          Guardar
        </Button>
      </Box>
    </Stack>
  );
}

// =========================
// List
// =========================
export default function ReceiptList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({ search: '', active: true });
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');
  const [snackSev, setSnackSev] = useState('info');

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(addTab({ label: 'Recibos de caja', path: '/payment/receipts' }));
  }, [dispatch]);

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(filters.search || ''), 400);
    return () => clearTimeout(t);
  }, [filters.search]);

  const handleFilter = (name, value) => setFilters((v) => ({ ...v, [name]: value }));
  const clearAll = () => setFilters({ search: '', active: true });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await PaymentCrud.receipts.list();
      const data = Array.isArray(res.data) ? res.data : (res.data?.results || []);

      let out = data.map((r) => ({
        id: r.id,
        ...r,
        payment_id: r.payment || r.payment_id || '',
        amount_num: Number(r.amount || 0),
      }));

      out = out.filter((r) => (filters.active ? r.is_active !== false : r.is_active === false));

      const s = (debouncedSearch || '').toLowerCase().trim();
      if (s) {
        out = out.filter((r) =>
          String(r.number || '').toLowerCase().includes(s) ||
          String(r.receipt_type || '').toLowerCase().includes(s) ||
          String(r.payment_id || '').toLowerCase().includes(s)
        );
      }

      setRows(out);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, filters.active]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditRow(null); setDialogOpen(true); };
  const openEdit = (row) => { setEditRow(row); setDialogOpen(true); };

  const handleSubmit = async (payload) => {
    try {
      if (editRow) await PaymentCrud.receipts.update(editRow.id, payload);
      else await PaymentCrud.receipts.create(payload);

      setDialogOpen(false);
      setEditRow(null);
      load();

      setSnackMsg(editRow ? 'Recibo actualizado' : 'Recibo creado');
      setSnackSev('success');
      setSnackOpen(true);
    } catch {
      setSnackMsg('No se pudo guardar el recibo');
      setSnackSev('error');
      setSnackOpen(true);
    }
  };

  const toggleActive = async (row) => {
    try {
      await PaymentCrud.receipts.update(row.id, { is_active: !row.is_active });
      load();
      setSnackMsg(row.is_active ? 'Recibo desactivado' : 'Recibo activado');
      setSnackSev('success');
      setSnackOpen(true);
    } catch {}
  };

  const removeRow = async (row) => {
    if (!window.confirm('Eliminar recibo de forma permanente?')) return;
    try {
      await PaymentCrud.receipts.remove(row.id);
      load();
      setSnackMsg('Recibo eliminado');
      setSnackSev('success');
      setSnackOpen(true);
    } catch {}
  };

  const columns = useMemo(() => ([
    {
      field: 'id',
      headerName: 'ID',
      width: 80,
      headerAlign: 'center',
      align: 'center',
      renderCell: (p) => (
        <CellCenterJustify justify="center">
          <Typography variant="caption" color="text.secondary">{p.value}</Typography>
        </CellCenterJustify>
      )
    },
    {
      field: 'number',
      headerName: 'Numero',
      flex: 1,
      minWidth: 180,
      renderCell: ({ value }) => (
        <CellCenter>
          <Typography sx={{ fontWeight: 800 }}>{value || '—'}</Typography>
        </CellCenter>
      )
    },
    {
      field: 'date',
      headerName: 'Fecha',
      width: 140,
      headerAlign: 'center',
      align: 'center',
      renderCell: ({ value }) => (
        <CellCenterJustify justify="center">
          <Typography>{formatDate(value) || '—'}</Typography>
        </CellCenterJustify>
      )
    },
    {
      field: 'amount',
      headerName: 'Monto',
      width: 150,
      headerAlign: 'right',
      align: 'right',
      renderCell: ({ row }) => (
        <CellCenterJustify justify="flex-end">
          <Typography sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
            {formatCurrency(row.amount)}
          </Typography>
        </CellCenterJustify>
      )
    },
    {
      field: 'receipt_type',
      headerName: 'Tipo',
      width: 160,
      headerAlign: 'center',
      align: 'center',
      renderCell: ({ value }) => (
        <CellCenterJustify justify="center">
          <Chip
            size="small"
            variant="outlined"
            label={value || 'N/D'}
          />
        </CellCenterJustify>
      )
    },
    {
      field: 'is_physical',
      headerName: 'Fisico',
      width: 120,
      headerAlign: 'center',
      align: 'center',
      renderCell: ({ value }) => (
        <CellCenterJustify justify="center">
          <Chip
            size="small"
            label={value ? 'Si' : 'No'}
            color={value ? 'success' : 'default'}
            variant="outlined"
          />
        </CellCenterJustify>
      )
    },
    {
      field: 'payment_id',
      headerName: 'Pago',
      width: 140,
      headerAlign: 'center',
      align: 'center',
      renderCell: ({ row }) => (
        <CellCenterJustify justify="center">
          {row.payment_id
            ? <Typography sx={{ fontWeight: 700 }}>#{row.payment_id}</Typography>
            : <Typography color="text.disabled">—</Typography>}
        </CellCenterJustify>
      )
    },
    {
      field: 'is_active',
      headerName: 'Estado',
      width: 130,
      headerAlign: 'center',
      align: 'center',
      renderCell: ({ value }) => (
        <CellCenterJustify justify="center">
          <Chip
            size="small"
            label={value === false ? 'Inactivo' : 'Activo'}
            color={value === false ? 'default' : 'success'}
            variant="outlined"
          />
        </CellCenterJustify>
      )
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 170,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: ({ row }) => (
        <CellCenterJustify justify="center">
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Editar">
              <IconButton size="small" onClick={() => openEdit(row)}>
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>

            {filters.active ? (
              <Tooltip title={row.is_active === false ? 'Activar' : 'Desactivar'}>
                <IconButton size="small" onClick={() => toggleActive(row)}>
                  {row.is_active === false
                    ? <CheckCircle fontSize="small" color="success" />
                    : <Cancel fontSize="small" color="warning" />
                  }
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Eliminar permanentemente">
                <IconButton size="small" color="error" onClick={() => removeRow(row)}>
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </CellCenterJustify>
      )
    },
  ]), [filters.active]);

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header limpio + chips mínimas */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2, width: '100%' }}>
        <Box sx={{ flexGrow: 1 }}>
          <MainText title="Recibos de caja" subtitle="Control rapido de recibos asociados a pagos." icon={ReceiptLong} width="auto" />
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {!!filters.search?.trim() && (
            <Chip
              size="small"
              label={`Busqueda: "${filters.search}"`}
              onDelete={() => handleFilter('search', '')}
            />
          )}

          {filters.active === false && (
            <Chip
              size="small"
              label="Mostrando inactivos"
              color="warning"
              onDelete={() => handleFilter('active', true)}
            />
          )}

          {!filters.search && filters.active && (
            <Chip size="small" label="Mostrando activos" color="info" />
          )}
        </Box>

        <CleanningButton onClick={clearAll} />
      </Box>

      {/* Barra de filtros compacta */}
      <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Buscar numero, pago o tipo..."
          value={filters.search}
          onChange={(e) => handleFilter('search', e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            )
          }}
          sx={{ width: { xs: '100%', sm: 320 } }}
        />

        <CustomSwitch
          name="active"
          label={filters.active ? 'Activos' : 'Inactivos'}
          initialValue={filters.active}
          onChange={(_, val) => handleFilter('active', val)}
        />

        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Refrescar">
            <IconButton onClick={load}><Refresh /></IconButton>
          </Tooltip>

          <Button
            onClick={openCreate}
            startIcon={<Add />}
            size="small"
            variant="contained"
            sx={{
              borderRadius: 2,
              px: 1.5,
              textTransform: 'none',
              fontWeight: 700,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none', transform: 'translateY(-1px)' }
            }}
          >
            Crear
          </Button>
        </Box>
      </Box>

      {/* Grid */}
      <Box sx={{ height: 480, width: '100%', mt: 1 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          disableColumnFilter
          disableColumnMenu
          pageSizeOptions={[10, 20, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
          rowHeight={50}
          columnHeaderHeight={50}
          sx={{
            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#10173c', color: '#fff' },
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: '#10173c !important',
              '&:hover, &:focus, &:active': { backgroundColor: '#10173c !important' }
            },
            '& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-iconButtonContainer, & .MuiDataGrid-menuIcon, & .MuiDataGrid-columnSeparator': {
              color: '#ffffff !important'
            },
            '& .MuiDataGrid-cell': { padding: '0 10px', borderBottom: '1px solid #f0f0f0' },
            '& .MuiDataGrid-row:hover': { cursor: 'pointer', backgroundColor: 'rgba(16, 23, 60, 0.08)' },
          }}
        />
      </Box>

      {/* Modal */}
      <CustomModal
        open={dialogOpen}
        setOpen={(v) => { setDialogOpen(v); if (!v) setEditRow(null); }}
        title={editRow ? 'Editar recibo' : 'Crear recibo'}
        component={ReceiptForm}
        componentProps={{ initial: editRow, onSubmit: handleSubmit }}
        width="520px"
      />

      {/* Snack */}
      <CustomSnackBar open={snackOpen} setOpen={setSnackOpen} message={snackMsg} severity={snackSev} />
    </Box>
  );
}
